import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const getAllAdminUsers = createAsyncThunk(
  'adminUsers/getAllAdminUsers',
  async ({ page, limit, search, status }, thunkAPI) => {
    try {
      let url = `${API_BASE_URL}auth/admin-users?page=${page}&limit=${limit}`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      if (status && status !== 'all') {
        url += `&status=${encodeURIComponent(status)}`;
      }

      // Retrieve token from localStorage or Redux state
      const token = localStorage.getItem('token') || thunkAPI.getState().auth.token;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // Add Bearer token
        },
        // withCredentials: true, // Remove if not using cookies
      });
      
      return {
        items: response.data.data.items,
        total: response.data.data.totalItems,
        currentPage: response.data.data.page,
        pageSize: limit,
        totalPages: Math.ceil(response.data.data.totalItems / limit),
        itemCount: response.data.data.items.length
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update other thunks similarly
export const updateUserStatus = createAsyncThunk(
  'adminUsers/updateUserStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}auth/admin-users/${id}/status`,
        { status },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      return { id, status, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteAdminUser = createAsyncThunk(
  'adminUsers/deleteAdminUser',
  async (userId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_BASE_URL}auth/admin-users/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      return { userId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState: {
    adminUsers: [],
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    itemCount: 0,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllAdminUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.adminUsers = action.payload.items;
        state.total = action.payload.total;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
        state.itemCount = action.payload.itemCount;
      })
      .addCase(getAllAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.adminUsers = state.adminUsers.map(user =>
          user.id === action.payload.id ? { ...user, status: action.payload.status } : user
        );
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.loading = false;
        state.adminUsers = state.adminUsers.filter((user) => user.id !== action.payload.userId);
        state.total -= 1;
        state.itemCount -= 1;
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default adminUsersSlice.reducer;